<template>
  <div class="genesis-page">
    <div class="background-header"></div>
    <div class="shell">
      <header class="topbar">
        <button class="back-btn" type="button" @click="$router.back()">
          <span class="back-arrow">&#8592;</span>
        </button>
        <h1 class="page-title">{{ t.genesisnode }}</h1>
        <div class="topbar-placeholder"></div>
      </header>

      <div class="level-list">
        <div class="level-card" v-for="level in levels" :key="level.name">
          <div class="level-card-inner">
            <div class="level-header">
              <div class="level-name">{{ level.name }}</div>
              <div class="level-tag" v-if="isGenesisNode">{{ t.genesisnode }}</div>
            </div>
            <van-progress
              style="margin-top: 12px;"
              :percentage="level.percent"
              stroke-width="8px"
              color="#e51d45"
              :show-pivot="false"
            />
            <div class="level-hint">{{ level.hint }}</div>
          </div>
        </div>
      </div>
    </div>

    <bottom-nav active-tab="team" />

    <div class="toast" v-if="toastVisible">{{ toastMsg }}</div>
  </div>
</template>

<script>
import BottomNav from '@/components/BottomNav.vue'
import { wallet } from '@/store/wallet'
import { getUser } from '@/api/auth'
import { getMessages } from '@/i18n'

const LEVEL_ORDER = ['C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7', 'C8', 'C9']
const LEVEL_THRESHOLDS = {
  C1: 5000,
  C2: 10000,
  C3: 50000,
  C4: 250000,
  C5: 750000,
  C6: 2000000,
  C7: 5000000,
  C8: 10000000,
  C9: 20000000,
}

const GENESIS_WHITELIST = [
  '0xe3f51904994b26df978741218aa0cedbccab3e3a',
  '0x3b535afa2a66606cb9f9f359d3fb930933519908',
  '0xa2cafd8989d23b0f99f853f4a1bc2d39c0fa8aa9',
  '0x552c8778a009933724b2e78709306913689bc093',
]

export default {
  name: 'GenesisNodePage',
  components: { BottomNav },
  data() {
    return {
      lang: localStorage.getItem('lang') || 'en',
      currentLevel: 'C2',
      smallArea: 0,
      levels: [],
      isGenesisNode: false,
      toastVisible: false,
      toastMsg: '',
    }
  },
  computed: {
    t() {
      return getMessages(this.lang)
    },
  },
  mounted() {
    this.lang = localStorage.getItem('lang') || 'en'
    this.loadData()
  },
  methods: {
    async loadData() {
      try {
        const res = await getUser()
        const u = res.data
        this.currentLevel = u.level || 'C0'
        this.isGenesisNode = (u.is_paid && u.in_pool) || GENESIS_WHITELIST.includes(wallet.address?.toLowerCase())
        const smallArea = parseFloat(u.small_area) || 0
        const t = this.t
        const upperLevels = LEVEL_ORDER.filter(name => name > this.currentLevel)
        this.levels = upperLevels.map(name => {
          const threshold = LEVEL_THRESHOLDS[name]
          const percent = Math.min(Math.round((smallArea / threshold) * 100), 100)
          const remaining = Math.max(0, threshold - smallArea)
          const hint = `${t.distance} ${name} ${t.stillNeed} ${remaining.toLocaleString()} ${t.minorPerformance}`
          return { name, percent, hint, isCurrent: false }
        })
      } catch (e) {
        console.error('loadData failed', e)
      }
    },
  }
}
</script>

<style lang="less" scoped>
.genesis-page {
  min-height: 100vh;
  color: #fff;
  background-image: url(../assets/home/background.png);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  padding-bottom: 90px;
  position: relative;
}

.background-header {
  width: 100%;
  height: 200px;
  background-image: url(../assets/home/background-header.png);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
}

.shell {
  max-width: 430px;
  margin: 0 auto;
  padding: 0 15px 24px;
  box-sizing: border-box;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  color: #e5dce8;
  font-size: 15px;
  cursor: pointer;
  padding: 4px 0;
}

.back-arrow {
  font-size: 18px;
  line-height: 1;
}

.page-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #fff;
}

.topbar-placeholder {
  width: 48px;
}

.level-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.level-card {
  border-radius: 10px;
  padding: 1px;
  background-image: url(../assets/home/level-border.png);
  background-size: 100% 100%;
  background-repeat: no-repeat;
}

.level-card-inner {
  border-radius: 10px;
  padding: 16px;
}

.level-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.level-name {
  font-family: 'DIN Alternate', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 54px;
  line-height: 1;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  background: linear-gradient(180deg, #FD656A 0%, #DF2340 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.level-tag {
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.3);
  white-space: nowrap;

  &--active {
    background: linear-gradient(148.49deg, rgba(243, 40, 90, 0.2) 0%, rgba(219, 48, 70, 0.126) 65.45%);
    color: #FF235A;
    border: 1px solid rgba(243, 40, 90, 0.4);
  }
}

.level-hint {
  margin-top: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

.toast {
  position: fixed;
  bottom: 110px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 20px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 14px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 200;
}
</style>
